// Idioma automático. Ordem: ?lang= na URL > escolha manual da pessoa > país pelo IP >
// idioma/fuso do navegador > português.
export type Idioma = "pt" | "es";

const CHAVE_MANUAL = "idioma_escolhido";
const CHAVE_IP = "idioma_por_ip";
const VALIDADE_IP_MS = 7 * 24 * 60 * 60 * 1000;

// Endpoint público da Cloudflare: devolve só o país (loc=BR). Sem chave e sem limite de uso.
const URL_PAIS = "https://www.cloudflare.com/cdn-cgi/trace";

// Países de língua espanhola atendidos pela central (América Latina e Espanha).
const PAISES_ES = new Set([
  "MX", "AR", "CO", "CL", "PE", "UY", "PY", "BO", "EC", "VE", "CR", "PA", "GT", "HN",
  "SV", "NI", "DO", "CU", "PR", "ES",
]);

const lerStorage = (chave: string) => {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
};

const gravarStorage = (chave: string, valor: string) => {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // modo privado ou storage bloqueado: segue sem guardar
  }
};

const valido = (valor: string | null): valor is Idioma => valor === "pt" || valor === "es";

export const idiomaDaUrl = (): Idioma | null => {
  const lang = new URLSearchParams(window.location.search).get("lang");
  return valido(lang) ? lang : null;
};

export const idiomaManual = (): Idioma | null => {
  const valor = lerStorage(CHAVE_MANUAL);
  return valido(valor) ? valor : null;
};

export const guardarIdiomaManual = (idioma: Idioma) => gravarStorage(CHAVE_MANUAL, idioma);

const idiomaPorIpEmCache = (): Idioma | null => {
  try {
    const { idioma, em } = JSON.parse(lerStorage(CHAVE_IP) || "{}");
    return valido(idioma) && Date.now() - em < VALIDADE_IP_MS ? idioma : null;
  } catch {
    return null;
  }
};

const idiomaDoNavegador = (): Idioma => {
  const linguas = navigator.languages?.length ? navigator.languages : [navigator.language];
  const primeira = linguas.find((l) => /^(pt|es)/i.test(l));
  if (primeira) return primeira.toLowerCase().startsWith("es") ? "es" : "pt";

  // navegador em inglês, por exemplo: o fuso ainda indica o país
  const fuso = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const fusoHispano =
    /^America\/(Mexico_City|Cancun|Monterrey|Tijuana|Bogota|Lima|Santiago|Argentina|Buenos_Aires|Montevideo|Asuncion|La_Paz|Guayaquil|Caracas|Costa_Rica|Panama|Guatemala|Tegucigalpa|El_Salvador|Managua|Santo_Domingo|Havana|Puerto_Rico)/.test(
      fuso
    ) || fuso === "Europe/Madrid";
  return fusoHispano ? "es" : "pt";
};

// Síncrono: usado pelo i18n ao iniciar.
export const idiomaInicial = (): Idioma =>
  idiomaDaUrl() || idiomaManual() || idiomaPorIpEmCache() || idiomaDoNavegador();

// Consulta o país pelo IP (uma vez a cada 7 dias) antes do primeiro render. Não espera mais
// que `limiteMs`: se a consulta demorar ou falhar, fica o idioma do navegador.
export const prepararIdioma = async (limiteMs = 1200): Promise<Idioma> => {
  const semConsulta = idiomaDaUrl() || idiomaManual() || idiomaPorIpEmCache();
  if (semConsulta) return semConsulta;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), limiteMs);
  try {
    const resposta = await fetch(URL_PAIS, { signal: controller.signal });
    const pais = (await resposta.text()).match(/^loc=([A-Z]{2})$/m)?.[1];
    if (pais) {
      const idioma: Idioma = PAISES_ES.has(pais) ? "es" : "pt";
      gravarStorage(CHAVE_IP, JSON.stringify({ idioma, em: Date.now() }));
      return idioma;
    }
  } catch {
    // sem rede, bloqueador ou demora: cai para o navegador
  } finally {
    clearTimeout(timer);
  }
  return idiomaDoNavegador();
};
